import { glass, LandingSection, Reveal } from "@/components/landing/reveal";
import { cn } from "@/lib/utils";

const COLS = [
  {
    name: "Mission",
    sub: "pcbx.ai · rscircuit",
    accent: true,
  },
  {
    name: "Schematic-gen",
    sub: "Flux · Circuit Mind · Protoflow",
    accent: false,
  },
  {
    name: "Redline tools",
    sub: "AllSpice-class review",
    accent: false,
  },
] as const;

const ROWS = [
  {
    label: "Job",
    values: [
      "GitHub-style hardware collab on KiCad SoT",
      "Generate or draft schematic from spec / prompt",
      "Visual redline and comments on ECAD",
    ],
  },
  {
    label: "Source of truth",
    values: [
      "KiCad in git. mind is architecture projection.",
      "Browser or desktop CAD becomes the board",
      "ECAD files plus overlay / Hub metadata",
    ],
  },
  {
    label: "Review",
    values: [
      "Design Reviews + circuit_diff + findings on refs",
      "Copilot inside the editor",
      "Visual diffs, in-design comments, checklists",
    ],
  },
  {
    label: "Control",
    values: ["Think Mission control + Jobs", "Generate-then-edit loop", "Actions / gate templates"],
  },
] as const;

export function LandingCompare() {
  return (
    <LandingSection
      id="compare"
      eyebrow="Compare"
      title="Collab on the board you already have."
      lede="Schematic generators start a board. Redline tools overlay a board. Mission is the GitHub-shaped loop around KiCad — Spec to Track, with agent findings that must land on the review."
    >
      <Reveal>
        <p className="sr-only">
          Mission versus schematic-gen tools (Flux, Circuit Mind, Protoflow) and redline tools (AllSpice)
        </p>
        <div className="grid gap-3 lg:hidden">
          {COLS.map((col, colIndex) => (
            <article key={col.name} className={`${glass} p-5`}>
              <h3 className={cn("text-[15px] font-semibold", col.accent ? "text-white" : "text-white/80")}>
                {col.name}
              </h3>
              <p className="mt-1 mb-4 text-[11px] text-white/45">{col.sub}</p>
              <dl className="space-y-3">
                {ROWS.map((row) => (
                  <div key={row.label}>
                    <dt className="text-[11px] tracking-[0.08em] text-white/45 uppercase">{row.label}</dt>
                    <dd className="mt-1 m-0 text-[13px] leading-[1.5] text-white/72">{row.values[colIndex]}</dd>
                  </div>
                ))}
              </dl>
            </article>
          ))}
        </div>
        <div className={`${glass} hidden overflow-x-auto lg:block`}>
          <table className="w-full border-collapse text-left text-[13px]">
            <thead>
              <tr className="border-b border-white/12">
                <th scope="col" className="w-[140px] px-4 py-4 font-medium text-white/40">
                  <span className="sr-only">Attribute</span>
                </th>
                {COLS.map((col) => (
                  <th key={col.name} scope="col" className="px-4 py-4 font-semibold">
                    <span className={col.accent ? "text-white" : "text-white/80"}>{col.name}</span>
                    <span className="mt-1 block text-[11px] font-normal tracking-[0.02em] text-white/45">
                      {col.sub}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => (
                <tr key={row.label} className="border-b border-white/8 last:border-b-0">
                  <th scope="row" className="px-4 py-3.5 align-top font-medium tracking-[0.04em] text-white/50 uppercase">
                    {row.label}
                  </th>
                  {row.values.map((value, i) => (
                    <td
                      key={COLS[i].name}
                      className={`px-4 py-3.5 align-top leading-[1.5] ${i === 0 ? "text-white/82" : "text-white/58"}`}
                    >
                      {value}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Reveal>
    </LandingSection>
  );
}
